import { DateTimeText } from "@nexus/ui";
import { Link } from "react-router-dom";
import { useMemo } from "react";

import { createColumnHelper } from "@tanstack/react-table";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Copy,
  Eye,
  EyeOff,
  FileDown,
  MoreVertical,
  PanelTopOpen,
  Pin,
  Pencil,
  SquareArrowOutUpRight,
  Plus,
  Share2,
  Trash2,
} from "lucide-react";

import { ListTable, ListTableSkeleton } from "@/components/ListTable";
import { PinDashboardButton } from "@/components/dashboard/PinDashboardButton";
import type { DashboardSort, SortOrder } from "@/hooks/dashboard/useDashboards";
import { dashboardTags, type DashboardRecord } from "@/lib/dashboard/types";
import { canViewDashboardAudience, dashboardOwner } from "@/lib/dashboard/dashboardOrigin";
import {
  Badge,
  TagList,
  IconButton,
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
  cn,
} from "@nexus/ui";
import { usePinnedDashboards } from "@/hooks/dashboard/usePinnedDashboards";
import { useOptionalAuthorization } from "@/contexts/AuthorizationContext";
import { useTenants } from "@/hooks/useTenants";
import { useGrantableAccounts } from "../useGrantableAccounts";
import { DashboardAudience } from "./DashboardAudience";
import { EXPORT_AS_FILE_LABEL } from "../workspaceImport";

export interface DashboardTableProps {
  dashboards: DashboardRecord[];
  presentation?: "standard" | "catalog";
  onOpen: (dashboard: DashboardRecord) => void;
  getOpenHref?: (dashboard: DashboardRecord) => string;
  showOpenAction?: boolean;
  onEdit?: (dashboard: DashboardRecord) => void;
  onDelete?: (dashboard: DashboardRecord) => void;
  onDuplicate?: (dashboard: DashboardRecord) => void;
  onInstantiate?: (dashboard: DashboardRecord) => void;
  onShare?: (dashboard: DashboardRecord) => void;
  /** Download it as a JSON file another platform can import. */
  onExport?: (dashboard: DashboardRecord) => void;
  onToggleDemoVisibility?: (dashboard: DashboardRecord) => void;
  /** Offer a pin on published dashboards (the person's shortlist). */
  pinnable?: boolean;
  showOwner?: boolean;
  sort: DashboardSort;
  order: SortOrder;
  onSort: (sort: DashboardSort) => void;
}

function StatusBadge({
  dashboard,
  catalog = false,
}: {
  dashboard: DashboardRecord;
  catalog?: boolean;
}) {
  // Same words as the editor's save status: never published, published with a pending draft, or up to date.
  const status =
    dashboard.state === "draft" ? "Draft" : dashboard.draft ? "Unpublished changes" : "Published";
  const tone = status === "Published" ? "neutral" : "warning";
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Badge tone={tone} dot className="border-0 bg-transparent px-0">
        {status}
      </Badge>
      {dashboard.protected && !catalog && <Badge tone="neutral">Protected</Badge>}
    </div>
  );
}

function TemplateAccessBadge({ dashboard }: { dashboard: DashboardRecord }) {
  return (
    <Badge tone="neutral">
      {dashboard.access?.owned
        ? dashboard.audience_grants?.length
          ? "Shared"
          : "Private"
        : "Shared with you"}
    </Badge>
  );
}

function SortableHeader({
  label,
  field,
  sort,
  order,
  onSort,
}: {
  label: string;
  field: DashboardSort;
  sort: DashboardSort;
  order: SortOrder;
  onSort: (sort: DashboardSort) => void;
}) {
  const active = sort === field;
  const SortIcon = active ? (order === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1.5 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
      aria-label={`Sort by ${label}${active ? `, currently ${order === "asc" ? "ascending" : "descending"}` : ""}`}
      onClick={() => onSort(field)}
    >
      {label}
      <SortIcon className="h-3.5 w-3.5" aria-hidden />
    </button>
  );
}

/**
 * The row's commands. Only the pin stays in the row (and only on hover or focus
 * until the dashboard is pinned, when it marks the row); everything else —
 * edit, open in a new tab, duplicate, delete — waits in one "⋯" menu, since the
 * row itself already opens the dashboard.
 */
function DashboardActions({
  dashboard,
  onOpen,
  getOpenHref,
  showOpenAction,
  onEdit,
  onDelete,
  onDuplicate,
  onInstantiate,
  onShare,
  onExport,
  onToggleDemoVisibility,
  pinnable,
}: Pick<
  DashboardTableProps,
  | "onOpen"
  | "getOpenHref"
  | "showOpenAction"
  | "onEdit"
  | "onDelete"
  | "onDuplicate"
  | "onInstantiate"
  | "onShare"
  | "onExport"
  | "onToggleDemoVisibility"
  | "pinnable"
> & {
  dashboard: DashboardRecord;
}) {
  const pins = usePinnedDashboards();
  const canPin =
    dashboard.kind !== "template" &&
    Boolean(pinnable) &&
    pins.available &&
    dashboard.state !== "draft";
  const pinned = canPin && pins.isPinned(dashboard.id);
  const canEdit = Boolean(onEdit) && dashboard.access?.editable !== false;
  const canDelete = Boolean(onDelete) && dashboard.access?.editable !== false;
  const hasMenu =
    showOpenAction ||
    canEdit ||
    getOpenHref ||
    onToggleDemoVisibility ||
    onDuplicate ||
    onExport ||
    onInstantiate ||
    (canEdit && onShare) ||
    canDelete;
  return (
    <div
      className="flex items-center justify-end gap-0.5"
      onClick={(event) => event.stopPropagation()}
    >
      {canPin ? (
        <PinDashboardButton
          dashboardId={dashboard.id}
          name={dashboard.name}
          className={cn(
            "size-8 rounded-md",
            !pinned &&
              "opacity-0 focus-visible:opacity-100 group-hover/row:opacity-100 group-focus-within/row:opacity-100 pointer-coarse:opacity-100",
          )}
        />
      ) : null}
      {hasMenu ? (
        <Menu>
          <MenuTrigger asChild>
            <IconButton label={`Actions for ${dashboard.name}`} className="size-8 rounded-md">
              <MoreVertical className="h-4 w-4" />
            </IconButton>
          </MenuTrigger>
          <MenuContent align="end" className="min-w-48">
            {canEdit && onShare && (
              <MenuItem disabled={dashboard.protected} onSelect={() => onShare(dashboard)}>
                <Share2 className="size-3.5 text-muted" aria-hidden />
                Share
              </MenuItem>
            )}
            {onInstantiate && (
              <MenuItem
                disabled={dashboard.state === "draft"}
                onSelect={() => onInstantiate(dashboard)}
              >
                <Plus className="size-3.5 text-muted" aria-hidden />
                Create dashboard
              </MenuItem>
            )}
            {showOpenAction && (
              <MenuItem onSelect={() => onOpen(dashboard)}>
                <PanelTopOpen className="h-3.5 w-3.5 text-muted" aria-hidden />
                Open
              </MenuItem>
            )}
            {getOpenHref && (
              <MenuItem asChild>
                <a
                  href={getOpenHref(dashboard)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(event) => event.stopPropagation()}
                >
                  <SquareArrowOutUpRight className="h-3.5 w-3.5 text-muted" aria-hidden="true" />
                  Open in new tab
                </a>
              </MenuItem>
            )}
            {canEdit && onEdit ? (
              <MenuItem disabled={dashboard.protected} onSelect={() => onEdit(dashboard)}>
                <Pencil className="h-3.5 w-3.5 text-muted" aria-hidden />
                {dashboard.protected ? "Edit (protected)" : "Edit"}
              </MenuItem>
            ) : null}
            {canPin ? (
              <MenuItem onSelect={() => pins.toggle(dashboard.id)}>
                <Pin className="h-3.5 w-3.5 text-muted" aria-hidden />
                {pinned ? "Unpin" : "Pin"}
              </MenuItem>
            ) : null}
            {onDuplicate || onExport || onToggleDemoVisibility ? <MenuSeparator /> : null}
            {onDuplicate && (
              <MenuItem
                disabled={dashboard.kind === "template" && dashboard.state === "draft"}
                onSelect={() => onDuplicate(dashboard)}
              >
                <Copy className="h-3.5 w-3.5 text-muted" aria-hidden />
                Duplicate
              </MenuItem>
            )}
            {onExport && (
              <MenuItem disabled={dashboard.state === "draft"} onSelect={() => onExport(dashboard)}>
                <FileDown className="h-3.5 w-3.5 text-muted" aria-hidden />
                {EXPORT_AS_FILE_LABEL}
              </MenuItem>
            )}
            {onToggleDemoVisibility && (
              <MenuItem onSelect={() => onToggleDemoVisibility(dashboard)}>
                {dashboard.status === "active" ? (
                  <EyeOff className="h-3.5 w-3.5 text-muted" aria-hidden />
                ) : (
                  <Eye className="h-3.5 w-3.5 text-muted" aria-hidden />
                )}
                {dashboard.status === "active" ? "Hide in demo" : "Show in demo"}
              </MenuItem>
            )}
            {canDelete && onDelete ? (
              <>
                <MenuSeparator />
                <MenuItem
                  tone="danger"
                  disabled={dashboard.protected}
                  onSelect={() => onDelete(dashboard)}
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden />
                  Delete
                </MenuItem>
              </>
            ) : null}
          </MenuContent>
        </Menu>
      ) : null}
    </div>
  );
}

const columnHelper = createColumnHelper<DashboardRecord>();

export function DashboardTable({
  dashboards,
  presentation = "standard",
  onOpen,
  getOpenHref,
  showOpenAction = false,
  onEdit,
  onDelete,
  onDuplicate,
  onInstantiate,
  onShare,
  onExport,
  onToggleDemoVisibility,
  pinnable = false,
  showOwner = false,
  sort,
  order,
  onSort,
}: DashboardTableProps) {
  const catalog = presentation === "catalog";
  const templates = dashboards.some((dashboard) => dashboard.kind === "template");
  const authorization = useOptionalAuthorization();
  const profile = authorization?.profile;
  const showAudience =
    !templates && dashboards.some((dashboard) => canViewDashboardAudience(dashboard, profile));
  const { data: accounts = [] } = useGrantableAccounts(
    !templates && authorization?.profile === "nagra-admin",
  );
  const { data: tenants = [] } = useTenants();
  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: () => (
          <SortableHeader
            label={templates ? "Template" : "Dashboard"}
            field="name"
            sort={sort}
            order={order}
            onSort={onSort}
          />
        ),
        cell: ({ row }) => (
          <div className="max-w-xl text-left">
            <div className="flex items-center gap-2">
              {getOpenHref ? (
                <Link
                  to={getOpenHref(row.original)}
                  aria-label={`Open ${row.original.draft?.name ?? row.original.name}`}
                  className="min-w-0 truncate rounded-control font-medium text-primary hover:underline focus-visible:outline-2 focus-visible:outline-focus"
                >
                  {row.original.draft?.name ?? row.original.name}
                </Link>
              ) : (
                <span className="min-w-0 truncate font-medium text-primary">
                  {row.original.draft?.name ?? row.original.name}
                </span>
              )}
            </div>
            {(row.original.draft?.description ?? row.original.description) ? (
              <span className="mt-0.5 block truncate text-xs text-muted">
                {row.original.draft?.description ?? row.original.description}
              </span>
            ) : null}
            <TagList
              tags={dashboardTags(row.original)}
              max={catalog ? 2 : 3}
              className={catalog ? "mt-1.5" : "mb-1 mt-2"}
            />
          </div>
        ),
      }),
      ...(showOwner
        ? [
            columnHelper.accessor(dashboardOwner, {
              id: "owner",
              header: "Owner",
              cell: ({ getValue }) => (
                <span className="whitespace-nowrap text-secondary">
                  {getValue() === "me" ? "Me" : "Others"}
                </span>
              ),
            }),
          ]
        : []),
      columnHelper.accessor("state", {
        id: "state",
        header: () => (
          <SortableHeader label="State" field="state" sort={sort} order={order} onSort={onSort} />
        ),
        cell: ({ row }) => <StatusBadge dashboard={row.original} catalog={catalog} />,
      }),
      ...(!catalog
        ? [
            columnHelper.display({
              id: "widgets",
              header: "Widgets",
              cell: ({ row }) => (
                <span className="tabular-nums text-secondary">
                  {(row.original.draft?.config ?? row.original.config).widgets?.length ?? 0}
                </span>
              ),
            }),
          ]
        : []),
      ...(templates
        ? [
            columnHelper.display({
              id: "access",
              header: "Access",
              cell: ({ row }) => <TemplateAccessBadge dashboard={row.original} />,
            }),
          ]
        : []),
      ...(showAudience
        ? [
            columnHelper.display({
              id: "audience",
              header: "Who can open it",
              cell: ({ row }) =>
                canViewDashboardAudience(row.original, profile) ? (
                  <DashboardAudience
                    dashboard={row.original}
                    accounts={accounts}
                    tenants={tenants}
                  />
                ) : null,
            }),
          ]
        : []),
      columnHelper.accessor("updated_at", {
        header: () => (
          <SortableHeader
            label="Updated"
            field="updated_at"
            sort={sort}
            order={order}
            onSort={onSort}
          />
        ),
        cell: ({ getValue }) => (
          <span className="whitespace-nowrap text-secondary">
            <DateTimeText
              value={getValue()}
              options={{
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hourCycle: "h23",
              }}
            />
          </span>
        ),
      }),
      ...(pinnable ||
      getOpenHref ||
      showOpenAction ||
      onEdit ||
      onDelete ||
      onDuplicate ||
      onInstantiate ||
      onShare ||
      onToggleDemoVisibility
        ? [
            columnHelper.display({
              id: "actions",
              header: () => <span className="sr-only">Actions</span>,
              cell: ({ row }) => (
                <DashboardActions
                  dashboard={row.original}
                  onOpen={onOpen}
                  getOpenHref={getOpenHref}
                  showOpenAction={showOpenAction}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onDuplicate={onDuplicate}
                  onInstantiate={onInstantiate}
                  onShare={onShare}
                  onExport={onExport}
                  onToggleDemoVisibility={onToggleDemoVisibility}
                  pinnable={pinnable}
                />
              ),
            }),
          ]
        : []),
    ],
    [
      catalog,
      getOpenHref,
      onDelete,
      onEdit,
      onDuplicate,
      onInstantiate,
      onShare,
      onExport,
      onToggleDemoVisibility,
      onOpen,
      onSort,
      order,
      pinnable,
      showOpenAction,
      sort,
      templates,
      showOwner,
      accounts,
      tenants,
      profile,
      showAudience,
    ],
  );

  return (
    <>
      <ListTable
        data={dashboards}
        columns={columns}
        getRowId={(dashboard) => dashboard.id}
        onRowClick={onOpen}
        rowHasLink={Boolean(getOpenHref)}
        rowAriaLabel={(dashboard) => `Open ${dashboard.draft?.name ?? dashboard.name}`}
        renderMobileCard={(dashboard) => (
          <>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 text-left">
                <h2 className="truncate text-sm font-semibold text-primary">
                  {getOpenHref ? (
                    <Link
                      to={getOpenHref(dashboard)}
                      aria-label={`Open ${dashboard.draft?.name ?? dashboard.name}`}
                      className="rounded-control hover:underline focus-visible:outline-2 focus-visible:outline-focus"
                    >
                      {dashboard.draft?.name ?? dashboard.name}
                    </Link>
                  ) : (
                    (dashboard.draft?.name ?? dashboard.name)
                  )}
                </h2>
                {(dashboard.draft?.description ?? dashboard.description) ? (
                  <p className="mt-0.5 line-clamp-2 text-xs text-muted">
                    {dashboard.draft?.description ?? dashboard.description}
                  </p>
                ) : null}
                <TagList
                  tags={dashboardTags(dashboard)}
                  max={catalog ? 2 : 3}
                  className={catalog ? "mt-1.5" : "mb-1 mt-2"}
                />
              </div>
              <div className="flex shrink-0 items-center">
                <DashboardActions
                  dashboard={dashboard}
                  onOpen={onOpen}
                  getOpenHref={getOpenHref}
                  showOpenAction={showOpenAction}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onDuplicate={onDuplicate}
                  onInstantiate={onInstantiate}
                  onShare={onShare}
                  onExport={onExport}
                  onToggleDemoVisibility={onToggleDemoVisibility}
                  pinnable={pinnable}
                />
              </div>
            </div>
            <dl className="mt-3 grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
              {showOwner ? (
                <div>
                  <dt className="text-muted">Owner</dt>
                  <dd className="mt-1 text-secondary">
                    {dashboardOwner(dashboard) === "me" ? "Me" : "Others"}
                  </dd>
                </div>
              ) : null}
              <div>
                <dt className="text-muted">State</dt>
                <dd className="mt-1">
                  <StatusBadge dashboard={dashboard} catalog={catalog} />
                </dd>
              </div>
              {templates && (
                <div>
                  <dt className="text-muted">Access</dt>
                  <dd className="mt-1">
                    <TemplateAccessBadge dashboard={dashboard} />
                  </dd>
                </div>
              )}
              {!catalog && (
                <div>
                  <dt className="text-muted">Widgets</dt>
                  <dd className="mt-1 text-secondary">
                    {(dashboard.draft?.config ?? dashboard.config).widgets?.length ?? 0}
                  </dd>
                </div>
              )}
              {showAudience && canViewDashboardAudience(dashboard, profile) && (
                <div className="col-span-2 min-w-0">
                  <dt className="text-muted">Who can open it</dt>
                  <dd className="mt-1">
                    <DashboardAudience
                      dashboard={dashboard}
                      accounts={accounts}
                      tenants={tenants}
                    />
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-muted">Updated</dt>
                <dd className="mt-1 whitespace-nowrap text-secondary">
                  <DateTimeText
                    value={dashboard.updated_at}
                    options={{
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      hourCycle: "h23",
                    }}
                  />
                </dd>
              </div>
            </dl>
          </>
        )}
      />
    </>
  );
}

export function DashboardTableSkeleton({ showOwner = false }: { showOwner?: boolean }) {
  return <ListTableSkeleton label="Loading dashboards" columnCount={showOwner ? 6 : 5} />;
}
